import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, ClipboardCheck, FileText, 
  Bell, User, LogOut, Menu, X, ChevronRight, Utensils, Pill, Heart
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI } from '../lib/api';
import { ChatBubble } from './ChatBubble';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

export function DashboardLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Navigation items
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/diet', label: 'Diet', icon: Utensils },
    { path: '/supplements', label: 'Supplements', icon: Pill },
    { path: '/lifestyle', label: 'Lifestyle', icon: Heart },
    { path: '/checkin', label: 'Check-in', icon: ClipboardCheck },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/profile', label: 'Profile', icon: User },
  ];

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const markAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  // Get current page title
  const getCurrentTitle = () => {
    const currentItem = navItems.find(item => item.path === location.pathname);
    return currentItem?.label || 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Mobile Header */}
      <header className="lg:hidden glass-nav sticky top-0 z-40 border-b border-[#E8A095]/10 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2" data-testid="mobile-sidebar-toggle">
            <Menu className="w-6 h-6 text-[#4A2C2A]" />
          </button>
          <img src={LOGO_URL} alt="Rejuven" className="h-8 w-auto" />
          <div className="relative">
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-2 -mr-2"
              data-testid="notifications-toggle"
            >
              <Bell className="w-6 h-6 text-[#4A2C2A]" />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <img src={LOGO_URL} alt="Rejuven" className="h-10 w-auto" />
                <button onClick={() => setSidebarOpen(false)}>
                  <X className="w-6 h-6 text-[#4A2C2A]" />
                </button>
              </div>
              <nav className="space-y-2">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive 
                          ? 'bg-[#E8A095]/20 text-[#4A2C2A]' 
                          : 'text-[#4A2C2A]/70 hover:bg-[#E8A095]/10'
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
              <div className="mt-8 pt-8 border-t border-[#E8A095]/10">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 text-[#D68282] hover:bg-[#D68282]/10 rounded-xl w-full"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-[#E8A095]/10">
          <div className="flex flex-col flex-1 p-6">
            <Link to="/dashboard" className="mb-10">
              <img src={LOGO_URL} alt="Rejuven" className="h-12 w-auto" />
            </Link>
            
            <nav className="flex-1 space-y-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive 
                        ? 'bg-[#E8A095]/20 text-[#4A2C2A]' 
                        : 'text-[#4A2C2A]/70 hover:bg-[#E8A095]/10'
                    }`}
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </Link>
                );
              })}
            </nav>

            <div className="pt-6 border-t border-[#E8A095]/10">
              <div className="flex items-center gap-3 px-4 py-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#E8A095]/20 flex items-center justify-center">
                  <span className="text-[#4A2C2A] font-medium">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#4A2C2A] truncate">{user?.name}</p>
                  <p className="text-sm text-[#4A2C2A]/50 truncate">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 text-[#D68282] hover:bg-[#D68282]/10 rounded-xl w-full"
                data-testid="desktop-logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64">
          {/* Desktop Header */}
          <header className="hidden lg:flex items-center justify-between px-8 h-20 border-b border-[#E8A095]/10 bg-white/50 backdrop-blur-sm sticky top-0 z-30">
            <h1 className="font-heading text-2xl text-[#4A2C2A]">
              {getCurrentTitle()}
            </h1>
            <div className="flex items-center gap-4">
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 hover:bg-[#E8A095]/10 rounded-full relative"
                  data-testid="desktop-notifications-toggle"
                >
                  <Bell className="w-5 h-5 text-[#4A2C2A]" />
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-xl border border-[#E8A095]/20 z-50">
                    <div className="p-4 border-b border-[#E8A095]/10 flex items-center justify-between">
                      <h3 className="font-medium text-[#4A2C2A]">Notifications</h3>
                      {unreadCount > 0 && (
                        <button 
                          onClick={markAllRead}
                          className="text-sm text-[#E8A095] hover:text-[#D68C82]"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="p-4 text-center text-[#4A2C2A]/50">No notifications</p>
                      ) : (
                        notifications.slice(0, 5).map((notification) => (
                          <div 
                            key={notification.id}
                            className={`p-4 border-b border-[#E8A095]/10 last:border-0 ${
                              !notification.read ? 'bg-[#E8A095]/5' : ''
                            }`}
                          >
                            <p className="font-medium text-[#4A2C2A] text-sm">{notification.title}</p>
                            <p className="text-sm text-[#4A2C2A]/60 mt-1">{notification.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>

          <div className="p-4 lg:p-8">
            {children}
          </div>
        </main>
      </div>
      
      {/* Floating Chat Bubble */}
      <ChatBubble />
    </div>
  );
}
